import { useEffect, useRef } from 'react';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';

import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

import { ComparedIndicator } from './comparisonUtils';
import {
  buildSegments,
  computeChange,
  ContributionSegment,
  CONTRIBUTOR_COLORS_DARK,
  CONTRIBUTOR_COLORS_LIGHT,
  formatChange,
  hatchBackgroundImage,
  NO_BREAKDOWN_BG,
  NO_BREAKDOWN_HATCH,
} from './impactUtils';

// Shared column template so the header row and every data row line up.
export const IMPACT_GRID_COLUMNS = 'minmax(220px, 1fr) 110px 90px 70px';

/**
 * Height of one dataset's contribution bar. The amount column uses the same value so the ds1/ds2
 * bars stay vertically aligned with their scores — keep these in step.
 */
const ROW_HEIGHT = '28px';

const formatAmount = (amount?: number) => (amount === undefined ? '—' : formatNumericalValue(amount));

const ContributionBar = ({ widthPct, segments }: { widthPct: number; segments: ContributionSegment[] }) => {
  const noBreakdownBg = useColorModeValue(NO_BREAKDOWN_BG.light, NO_BREAKDOWN_BG.dark);
  const noBreakdownHatch = useColorModeValue(NO_BREAKDOWN_HATCH.light, NO_BREAKDOWN_HATCH.dark);

  const hasSegments = segments.length > 0;
  const isEmptyBar = widthPct === 0;

  return (
    <Box flex={1} minW={0}>
      <Box width={`${widthPct}%`} minW={isEmptyBar ? 0 : '6px'}>
        {hasSegments ? (
          <Flex h={ROW_HEIGHT} gap="2px">
            {/* Magnitude, not the signed share: flex-grow rejects a negative value and falls back
                to 0, which collapsed every avoided-burden contributor to the 2px minimum and handed
                its width to the positive ones. The sign is still carried in the tooltip's %. */}
            {segments.map((segment) => (
              <Box
                key={segment.key}
                flexGrow={Math.abs(segment.share)}
                flexBasis={0}
                minW="2px"
                borderRadius="sm"
                bg={segment.color}
              />
            ))}
          </Flex>
        ) : (
          // A score with no contributor breakdown. There is no pending state to distinguish any
          // more: contributions arrive with the scores in the one request, so a bar is either
          // rendered with its data or the whole tab is still loading.
          <Box
            h={ROW_HEIGHT}
            borderRadius="sm"
            bg={isEmptyBar ? 'transparent' : noBreakdownBg}
            backgroundImage={isEmptyBar ? undefined : hatchBackgroundImage(noBreakdownHatch)}
          />
        )}
      </Box>
    </Box>
  );
};

// Shared so the tooltip's header row and every contributor line stay aligned.
const CONTRIBUTOR_GRID_COLUMNS = '34px 1fr 72px 90px';

/**
 * One dataset's contributor at a given rank. Always renders its ds1/ds2 label so a pair is readable
 * even when only one dataset has a contributor at this rank (the other shows a dash).
 */
const ContributorLine = ({ label, segment }: { label: string; segment?: ContributionSegment }) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const labelColor = useColorModeValue('gray.500', 'gray.500');

  return (
    <Grid templateColumns={CONTRIBUTOR_GRID_COLUMNS} columnGap={3} fontSize="sm" alignItems="center">
      <Text fontSize="xs" color={labelColor}>
        {label}
      </Text>
      {segment ? (
        <>
          <Text isTruncated title={segment.label}>
            {segment.label}
          </Text>
          <Text color={grayText} textAlign="right" whiteSpace="nowrap">
            {(segment.share * 100).toFixed(1)}%
          </Text>
          <Text textAlign="right">{formatNumericalValue(segment.impact)}</Text>
        </>
      ) : (
        <>
          <Text color={labelColor}>—</Text>
          <Text color={labelColor} textAlign="right">
            —
          </Text>
          <Text color={labelColor} textAlign="right">
            —
          </Text>
        </>
      )}
    </Grid>
  );
};

/** How long the contributor popover must stay open before it counts as intentional. */
const CONTRIBUTORS_VIEWED_DELAY_MS = 500;

type Props = {
  indicator: ComparedIndicator;
  /** ds1/ds2 ids, for the contributor-viewed event only — no data is fetched from this row. */
  datasetIds: string[];
  lciaMethod?: string;
};

const ImpactCategoryRow = ({ indicator, datasetIds, lciaMethod }: Props) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const subGray = useColorModeValue('gray.500', 'gray.500');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const headerColor = useColorModeValue('gray.600', 'gray.400');
  const contributorColors = useColorModeValue(CONTRIBUTOR_COLORS_LIGHT, CONTRIBUTOR_COLORS_DARK);

  const segmentsA = buildSegments(indicator.contributions[0], contributorColors);
  const segmentsB = buildSegments(indicator.contributions[1], contributorColors);

  const [amountA, amountB] = indicator.amounts;
  const maxAmount = Math.max(Math.abs(amountA ?? 0), Math.abs(amountB ?? 0));
  const widthPct = (amount?: number) => (maxAmount > 0 && amount !== undefined ? (Math.abs(amount) / maxAmount) * 100 : 0);

  const changeText = formatChange(computeChange(amountA, amountB));

  const rankCount = Math.max(segmentsA.length, segmentsB.length);
  const hasContributors = rankCount > 0;

  // Contributions now arrive with the scores, so a side is either present or genuinely empty —
  // there is no per-side failure to report any more, the whole tab fails together.
  const emptySides = [
    { label: 'ds1', amount: amountA, segments: segmentsA },
    { label: 'ds2', amount: amountB, segments: segmentsB },
  ]
    .filter((side) => side.amount !== undefined && side.segments.length === 0)
    .map((side) => side.label);

  // Debounced so passing the cursor over rows on the way somewhere else isn't counted as a view.
  const viewedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearViewedTimer = () => {
    if (viewedTimerRef.current) {
      clearTimeout(viewedTimerRef.current);
      viewedTimerRef.current = null;
    }
  };

  useEffect(() => clearViewedTimer, []);

  const onContributorsOpen = () => {
    clearViewedTimer();
    viewedTimerRef.current = setTimeout(() => {
      mixpanel.track(MIXPANEL_EVENTS.COMPARISON_CONTRIBUTORS_VIEWED, {
        category: indicator.category,
        indicator: indicator.indicator,
        lcia_method: lciaMethod,
        dataset_ids: datasetIds,
        has_contributors: hasContributors,
      });
    }, CONTRIBUTORS_VIEWED_DELAY_MS);
  };

  return (
    <Box py={4} borderBottomWidth="1px" borderColor={borderColor}>
      <Stack spacing={0} mb={3}>
        <Heading size="sm">{indicator.category}</Heading>
        <Text fontSize="sm" fontWeight="medium" color={grayText}>
          {indicator.indicator}
        </Text>
      </Stack>

      <Popover trigger="hover" placement="top" isLazy onOpen={onContributorsOpen} onClose={clearViewedTimer}>
        <PopoverTrigger>
          <Grid templateColumns={IMPACT_GRID_COLUMNS} columnGap={6} alignItems="center">
            <GridItem>
              <Stack spacing={2}>
                <HStack spacing={2} minH={ROW_HEIGHT}>
                  <Text w="30px" fontSize="xs" fontWeight="medium" color={subGray}>
                    ds1
                  </Text>
                  <ContributionBar widthPct={widthPct(amountA)} segments={segmentsA} />
                </HStack>
                <HStack spacing={2} minH={ROW_HEIGHT}>
                  <Text w="30px" fontSize="xs" fontWeight="medium" color={subGray}>
                    ds2
                  </Text>
                  <ContributionBar widthPct={widthPct(amountB)} segments={segmentsB} />
                </HStack>
              </Stack>
            </GridItem>
            <GridItem>
              <Stack spacing={2}>
                <Text fontSize="sm" fontWeight="medium" h={ROW_HEIGHT} lineHeight={ROW_HEIGHT}>
                  {formatAmount(amountA)}
                </Text>
                <Text fontSize="sm" fontWeight="medium" h={ROW_HEIGHT} lineHeight={ROW_HEIGHT}>
                  {formatAmount(amountB)}
                </Text>
              </Stack>
            </GridItem>
            <GridItem>
              <Text fontSize="sm" color={grayText} whiteSpace="nowrap">
                {indicator.unit}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="sm" fontWeight="semibold">
                {changeText}
              </Text>
            </GridItem>
          </Grid>
        </PopoverTrigger>
        <PopoverContent w="480px" maxW="90vw">
          <PopoverArrow />
          <PopoverBody p={4}>
            <Stack spacing={0} mb={3}>
              <Heading size="sm">{indicator.category}</Heading>
              <Text fontSize="sm" fontWeight="medium" color={grayText}>
                {indicator.indicator}
              </Text>
            </Stack>
            {/* 6px between sections, per design — Chakra's scale step 1.5. */}
            <Stack spacing={1.5}>
              {hasContributors && (
                <>
                  {/* Mirrors the main table's header row: uppercase, wider tracking and a rule
                        beneath it, which this card was missing. */}
                  <Grid
                    templateColumns={CONTRIBUTOR_GRID_COLUMNS}
                    columnGap={3}
                    fontSize="xs"
                    letterSpacing="wider"
                    textTransform="uppercase"
                    color={headerColor}
                    pl="15px"
                    pb={2}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                  >
                    <Text />
                    <Text>Contributor</Text>
                    <Text textAlign="right">Share</Text>
                    <Text textAlign="right" fontWeight="semibold">
                      Impact ({indicator.unit})
                    </Text>
                  </Grid>
                  {Array.from({ length: rankCount }, (_, rank) => (
                    <HStack key={rank} align="stretch" spacing={3}>
                      <Box
                        w="3px"
                        flexShrink={0}
                        borderRadius="full"
                        bg={contributorColors[Math.min(rank, contributorColors.length - 1)]}
                      />
                      <Stack flex={1} minW={0} spacing={1}>
                        <ContributorLine label="ds1" segment={segmentsA[rank]} />
                        <ContributorLine label="ds2" segment={segmentsB[rank]} />
                      </Stack>
                    </HStack>
                  ))}
                </>
              )}
              {emptySides.length > 0 && (
                <Text fontSize="xs" color={subGray} pt={1}>
                  {emptySides.join(' and ')} {emptySides.length > 1 ? 'have' : 'has'} no contributor breakdown for this
                  category.
                </Text>
              )}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default ImpactCategoryRow;
